import api from './client';

export type TableStatEntry = {
  schema: string;
  table_name: string;
  total_size: string;
  table_size: string;
  index_size: string;
  total_size_bytes: number;
  row_count: number;
  dead_rows: number;
  seq_scan: number;
  idx_scan: number;
  n_tup_hot_upd: number;
  n_tup_upd: number;
  heap_blks_hit: number;
  heap_blks_read: number;
  last_vacuum: string | null;
  last_autovacuum: string | null;
  last_analyze: string | null;
  last_autoanalyze: string | null;
};

export type QueryLogEntry = {
  id: number;
  started_at: string;
  finished_at: string;
  duration_ms: number;
  query: string;
  params_count: number;
  params?: unknown[];
  row_count: number;
  status: 'success' | 'error';
  error: string | null;
  service?: string;
  buf_hit?: number;
  buf_read?: number;
  buf_dirtied?: number;
  buf_written?: number;
  temp_read?: number;
  temp_written?: number;
  io_read_ms?: number;
  endpoint?: string;
  method?: string;
  source?: string;
};

export type ActiveSessionEntry = {
  source: 'main' | 'investor';
  investorName: string | null;
  investorUuid: string | null;
  sessionId: string;
  userId: number;
  username: string | null;
  email: string | null;
  clientType: 'web' | 'mobile' | 'unknown';
  deviceLabel: string | null;
  browserLabel: string | null;
  osLabel: string | null;
  loginAt: string;
  lastSeenAt: string;
  lastPath: string | null;
  lastMethod: string | null;
  lastStatusCode: number | null;
  ip: string | null;
  userAgent: string | null;
  appVersion: string | null;
  requestCount: number;
  errorCount: number;
  lastErrorAt: string | null;
  lastErrorPath: string | null;
  longestRequestMs: number;
  longestRequestPath: string | null;
  ttlSeconds: number;
};

export type QueueJobEntry = {
  id: string;
  queue: string;
  jobName: string;
  state: 'waiting' | 'active' | 'completed' | 'failed' | 'delayed' | 'paused' | 'waiting-children' | 'unknown';
  subject: string | null;
  recipients: string[];
  attemptsMade: number;
  progress: number | Record<string, unknown> | null;
  queuedAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  failedReason: string | null;
  provider: string | null;
  messageId: string | null;
};

export type QueueOverview = {
  queues: Array<{
    queue: string;
    counts: Record<string, number>;
  }>;
  totalCounts: Record<string, number>;
  jobs: QueueJobEntry[];
};

export type QueryStats = {
  total: number;
  errors: number;
  avg_ms: number;
  max_ms: number;
  total_rows: number;
  long_running: number;
};

export type QueryFilter = 'all' | 'slow' | 'error' | string;

export type QueryLogsResponse = {
  stats: QueryStats;
  logs: QueryLogEntry[];
};

export async function fetchQueryDates(): Promise<string[]> {
  const res = await api.get<string[]>('/monitoring/queries/dates');
  return res.data;
}

export type QueryStatsResponse = { stats: QueryStats; total: number };

export async function fetchQueryStats(date?: string, filter: QueryFilter = 'all'): Promise<QueryStatsResponse> {
  const res = await api.get<QueryStatsResponse>('/monitoring/queries/stats', { params: { date, filter } });
  return res.data;
}

export type QueryLogsPageResponse = { logs: QueryLogEntry[]; total: number };

export async function fetchQueryLogsPage(
  date: string | undefined,
  offset: number,
  limit: number,
  filter: QueryFilter = 'all',
): Promise<QueryLogsPageResponse> {
  const res = await api.get<QueryLogsPageResponse>('/monitoring/queries', { params: { date, offset, limit, filter } });
  return res.data;
}

export async function clearQueryLogs(date?: string): Promise<void> {
  await api.delete('/monitoring/queries', { params: date ? { date } : undefined });
}

export interface ExplainResult {
  plan: string;
  buffers: Record<string, number> | null;
  mode: 'analyze' | 'plan';
  stats: {
    planningTimeMs: number | null;
    executionTimeMs: number | null;
    estimatedRows: number | null;
    actualRows: number | null;
    totalCostStart: number | null;
    totalCostEnd: number | null;
    nodeCounts: Record<string, number>;
  };
  warnings: string[];
}

export async function explainQuery(query: string, params?: unknown[]): Promise<ExplainResult> {
  const res = await api.post<ExplainResult>('/monitoring/queries/explain', { query, params });
  return res.data;
}

export async function fetchTableStats(): Promise<TableStatEntry[]> {
  const res = await api.get<TableStatEntry[]>('/monitoring/tables');
  return res.data;
}

export async function fetchActiveSessions(): Promise<ActiveSessionEntry[]> {
  const res = await api.get<ActiveSessionEntry[]>('/monitoring/sessions');
  return res.data;
}

export async function fetchQueueOverview(): Promise<QueueOverview> {
  const res = await api.get<QueueOverview>('/monitoring/queue');
  return res.data;
}

export type TableColumn = { column_name: string; data_type: string };

export type TableDataResponse = {
  columns: TableColumn[];
  total: number;
  rows: Record<string, unknown>[];
};

export type TableSchemaColumn = {
  column_name: string;
  data_type: string;
  udt_name: string;
  character_maximum_length: number | null;
  numeric_precision: number | null;
  numeric_scale: number | null;
  is_nullable: string;
  column_default: string | null;
};

export type TableSchemaIndex = {
  index_name: string;
  is_unique: boolean;
  is_primary: boolean;
  index_size: string;
  index_size_bytes: number;
  columns: string;
  index_type: string;
  idx_scan: number | null;
  idx_tup_read: number | null;
  idx_tup_fetch: number | null;
  idx_blks_hit: number | null;
  idx_blks_read: number | null;
};

export type TableSchemaStats = {
  n_live_tup: number;
  n_dead_tup: number;
  n_mod_since_analyze: number;
  seq_scan: number;
  seq_tup_read: number;
  idx_scan: number;
  idx_tup_fetch: number;
  n_tup_ins: number;
  n_tup_upd: number;
  n_tup_hot_upd: number;
  n_tup_del: number;
  heap_blks_hit: number | null;
  heap_blks_read: number | null;
  idx_blks_hit: number | null;
  idx_blks_read: number | null;
  last_vacuum: string | null;
  last_autovacuum: string | null;
  last_analyze: string | null;
  last_autoanalyze: string | null;
};

export type TableSchemaConstraint = {
  constraint_name: string;
  constraint_type: string;
  column_name: string;
  foreign_table: string | null;
  foreign_column: string | null;
};

export type TableSchemaResponse = {
  columns: TableSchemaColumn[];
  indexes: TableSchemaIndex[];
  stats: TableSchemaStats | null;
  constraints: TableSchemaConstraint[];
};

export async function fetchTableSchema(schema: string, table: string): Promise<TableSchemaResponse> {
  const res = await api.get<TableSchemaResponse>('/monitoring/table-schema', { params: { schema, table } });
  return res.data;
}

export async function fetchTableData(
  schema: string,
  table: string,
  offset: number,
  limit: number,
): Promise<TableDataResponse> {
  const res = await api.get<TableDataResponse>('/monitoring/table-data', {
    params: { schema, table, offset, limit },
  });
  return res.data;
}

export type PerformanceMetrics = {
  pool: {
    max: number;
    total: number;
    idle: number;
    waiting: number;
    active: number;
  };
  connections: {
    total: number; active: number; idle: number; idle_in_tx: number;
    waiting: number; max_connections: number;
    long_running_count: number; long_running_max_s: number;
  };
  cache: { blks_hit: number; blks_read: number; blks_total: number };
  temp_files: { count: number; bytes: number };
  table_health: {
    seq_scan: number; idx_scan: number;
    live_tuples: number; dead_tuples: number;
    tup_inserted: number; tup_updated: number; tup_deleted: number;
    unused_indexes: number;
  };
  bgwriter: {
    checkpoints_timed: number; checkpoints_req: number;
    checkpoint_write_ms: number; checkpoint_sync_ms: number;
    buffers_checkpoint: number; buffers_clean: number;
    buffers_backend: number; buffers_alloc: number; buffers_backend_fsync: number;
  };
  replication: {
    replica_count: number; max_write_lag: string | null;
    max_flush_lag: string | null; max_replay_lag: string | null;
    max_replay_lag_bytes: number;
  };
  locks: { blocked_queries: number; deadlocks: number; conflicts: number };
  database: {
    xact_commit: number; xact_rollback: number;
    db_size_bytes: number; db_size: string;
    tup_returned: number; tup_fetched: number;
    tup_inserted: number; tup_updated: number; tup_deleted: number;
  };
  redis: {
    available: boolean; connected: boolean;
    version: string | null; mode: string | null;
    uptime_seconds: number;
    connected_clients: number;
    used_memory: number; used_memory_human: string | null;
    used_memory_peak: number; used_memory_peak_human: string | null;
    maxmemory: number; maxmemory_human: string | null;
    evicted_keys: number;
    keyspace_hits: number; keyspace_misses: number; hit_rate: number | null;
    total_commands_processed: number;
    instantaneous_ops_per_sec: number;
    db0_keys: number; db0_expires: number; dbsize: number;
  };
  server: {
    hostname: string; platform: string; release: string;
    uptime_seconds: number; cpu_count: number;
    load_avg_1: number; load_avg_5: number; load_avg_15: number;
    memory: {
      total_bytes: number; free_bytes: number; used_bytes: number; used_pct: number;
    };
    swap: {
      total_bytes: number; free_bytes: number; used_bytes: number; used_pct: number;
    };
    disk_source?: string;
    disks?: Array<{ filesystem: string; mountpoint: string; type: string; available: boolean; total_bytes: number; used_bytes: number; available_bytes: number; used_pct: number }>;
    disk: {
      filesystem: string; mountpoint: string;
      total_bytes: number; used_bytes: number; available_bytes: number; used_pct: number;
    };
    io: {
      device: string;
      reads_completed: number; reads_merged: number; sectors_read: number; read_time_ms: number;
      writes_completed: number; writes_merged: number; sectors_written: number; write_time_ms: number;
      io_in_progress: number; io_time_ms: number; weighted_io_time_ms: number;
    };
  };
  slow_queries: Array<{
    query: string; calls: number;
    total_exec_time_ms: number; mean_exec_time_ms: number; stddev_exec_time_ms: number;
    rows: number; shared_blks_hit: number; shared_blks_read: number;
  }> | null;
};

export async function fetchPerformanceMetrics(): Promise<PerformanceMetrics> {
  const res = await api.get<PerformanceMetrics>('/monitoring/performance');
  return res.data;
}

export type IndexStatEntry = {
  schema: string;
  table_name: string;
  index_name: string;
  index_size: string;
  index_size_bytes: number;
  table_size: string;
  table_size_bytes: number;
  idx_scan: number;
  idx_tup_read: number;
  idx_tup_fetch: number;
  idx_blks_hit: number;
  idx_blks_read: number;
  index_def: string;
  index_type: string;
  is_unique: boolean;
  is_primary: boolean;
  columns: string | null;
  table_live_rows: number;
  table_dead_rows: number;
};

/** @deprecated use fetchIndexStats */
export type UnusedIndexEntry = IndexStatEntry;

export async function fetchIndexStats(unusedOnly = false): Promise<IndexStatEntry[]> {
  const res = await api.get<IndexStatEntry[]>('/monitoring/index-stats', {
    params: unusedOnly ? { unused_only: 'true' } : undefined,
  });
  return res.data;
}

/** @deprecated use fetchIndexStats(true) */
export async function fetchUnusedIndexes(): Promise<IndexStatEntry[]> {
  return fetchIndexStats(true);
}

export type RedisDatabaseInfo = {
  db: number;
  keys: number;
  expires: number;
};

export type RedisOverviewResponse = {
  selected_db: number;
  dbsize: number;
  databases: RedisDatabaseInfo[];
  info: {
    redis_version: string | null;
    redis_mode: string | null;
    uptime_in_seconds: number;
    connected_clients: number;
    used_memory_human: string | null;
    instantaneous_ops_per_sec: number;
    keyspace_hits: number;
    keyspace_misses: number;
    evicted_keys: number;
  };
};

export type RedisKeyListItem = {
  key: string;
  type: string;
  ttl_seconds: number;
  memory_usage: number;
  preview: string;
};

export type RedisKeysResponse = {
  cursor: string;
  has_more: boolean;
  items: RedisKeyListItem[];
};

export type RedisKeyDetail = {
  key: string;
  type: string;
  ttl_seconds: number;
  memory_usage: number;
  value: unknown;
};

export type RedisExportResponse = {
  exported_at: string;
  db: number;
  pattern: string;
  count: number;
  items: Array<{
    key: string;
    type: string;
    ttl_seconds: number;
    value: unknown;
  }>;
};

export async function fetchRedisOverview(db: number): Promise<RedisOverviewResponse> {
  const res = await api.get<RedisOverviewResponse>('/monitoring/redis/overview', { params: { db } });
  return res.data;
}

export async function fetchRedisKeys(
  db: number,
  pattern: string,
  cursor = '0',
  count = 100,
): Promise<RedisKeysResponse> {
  const res = await api.get<RedisKeysResponse>('/monitoring/redis/keys', {
    params: { db, pattern, cursor, count },
  });
  return res.data;
}

export async function fetchRedisKeyDetail(db: number, key: string): Promise<RedisKeyDetail> {
  const res = await api.get<RedisKeyDetail>('/monitoring/redis/key', { params: { db, key } });
  return res.data;
}

export async function updateRedisKey(
  db: number,
  key: string,
  payload: { type: string; ttl_seconds: number | null; value: unknown },
): Promise<void> {
  await api.put('/monitoring/redis/key', payload, { params: { db, key } });
}

export async function deleteRedisKey(db: number, key: string): Promise<void> {
  await api.delete('/monitoring/redis/key', { params: { db, key } });
}

export async function clearRedisKeys(db: number, pattern: string): Promise<{ success: boolean; deleted: number }> {
  const res = await api.delete<{ success: boolean; deleted: number }>('/monitoring/redis/keys', {
    params: { db, pattern },
  });
  return res.data;
}

export async function exportRedisKeys(db: number, pattern: string): Promise<RedisExportResponse> {
  const res = await api.get<RedisExportResponse>('/monitoring/redis/export', {
    params: { db, pattern },
  });
  return res.data;
}

export async function persistRedisKey(db: number, key: string): Promise<void> {
  await api.post('/monitoring/redis/key/persist', null, { params: { db, key } });
}

export type WaitingQueryEntry = {
  pid: number;
  usename: string;
  application_name: string;
  client_addr: string | null;
  state: string;
  wait_event_type: string;
  wait_event: string;
  duration_s: number;
  query: string;
  blocking_pids: number[];
};

export async function fetchWaitingQueries(): Promise<WaitingQueryEntry[]> {
  const res = await api.get<WaitingQueryEntry[]>('/monitoring/waiting-queries');
  return res.data;
}

export type LongRunningQueryEntry = {
  pid: number;
  usename: string;
  application_name: string;
  client_addr: string | null;
  state: string;
  wait_event_type: string | null;
  wait_event: string | null;
  duration_s: number;
  query: string;
  blocking_pids: number[];
};

export async function fetchLongRunningQueries(): Promise<LongRunningQueryEntry[]> {
  const res = await api.get<LongRunningQueryEntry[]>('/monitoring/long-running-queries');
  return res.data;
}

export type WorkbenchWorkspace = {
  id: string;
  name: string;
  query: string;
  limit: number;
};

export type WorkbenchExecutionResult = {
  rows: Record<string, unknown>[];
  rowCount: number;
  command: string;
  fields?: string[];
};

export async function executeWorkbenchQuery(query: string, limit: number): Promise<WorkbenchExecutionResult[]> {
  const res = await api.post<WorkbenchExecutionResult[]>('/monitoring/workbench/execute', { query, limit });
  return res.data;
}

export async function fetchWorkbenchWorkspaces(): Promise<WorkbenchWorkspace[]> {
  const res = await api.get<WorkbenchWorkspace[]>('/monitoring/workbench');
  return res.data;
}

export async function saveWorkbenchWorkspaces(workspaces: WorkbenchWorkspace[]): Promise<void> {
  await api.post('/monitoring/workbench', { workspaces });
}

export async function fetchWorkbenchSchema(): Promise<Record<string, string[]>> {
  const res = await api.get<Record<string, string[]>>('/monitoring/workbench/schema');
  return res.data;
}
